"use client";

import React, { useCallback } from "react";
import { useRouter } from "next/navigation";
import { ContactDetailsTab as ReusableContactDetailsTab } from "@/components/shared/detail-tabs/ContactDetailsTab";
import { useContact } from "@/lib/hooks/use-api";
import { useEntityContextOptional } from "@/components/shared/UniversalDetailPage/contexts/EntityContextProvider";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Ticket } from "@/lib/types/database";

/**
 * Ticket Contact Details Tab Component
 *
 * Wraps the reusable ContactDetailsTab with ticket-specific context.
 * ContactDetailsTab will automatically use contact_id from EntityContext.
 */
export const ContactDetailsTab = React.memo(({ item }: TabContentProps<Ticket>) => {
  const router = useRouter();
  
  // Get contact_id from EntityContext or fall back to item
  const entityContext = useEntityContextOptional();
  const rawContactId = item.contact_id ?? entityContext?.entityData?.contact_id;
  const contactId =
    typeof rawContactId === "string" && rawContactId ? rawContactId : null;

  const { data: contact } = useContact(contactId ?? "", { enabled: !!contactId });

  const handleViewFullContact = useCallback(
    (id: string) => {
      router.push(`/contacts/${id}`);
    },
    [router]
  );

  return (
    <ReusableContactDetailsTab
      contactData={contact}
      onViewFullContact={handleViewFullContact}
      showDescription="Full contact information for the ticket requester"
    />
  );
});

ContactDetailsTab.displayName = "ContactDetailsTab";
